import { DependencyList, useEffect } from 'react';

import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetCumulativeOrderList } from 'src/actions';
import { GetOrderListQueryParamsDto } from 'src/dtos';
import { Order } from 'src/models';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';
import {
  UseCumulativeOrderListSelectorReturnType,
  useCumulativeOrderListSelector,
} from './useCumulativeOrderListSelector';

interface UseCumulativeOrdersReturnType extends UseCumulativeOrderListSelectorReturnType {}

export interface UseCumulativeOrdersOptions extends CustomHookBaseOptions {
  refetchOnChange?: boolean;
  queryParamsDto?: Omit<GetOrderListQueryParamsDto, 'pageIndex' | 'pageSize'>;
  dependency?: DependencyList;
}

export const useCumulativeOrders = (
  { skip, refetchOnChange, queryParamsDto, dependency = [] }: UseCumulativeOrdersOptions = { skip: false },
): UseCumulativeOrdersReturnType => {
  const dispatch = useDispatch();
  const orderListRaw: Order[] | undefined = useSelector((state: any) => state.orders.cumulativeList?.data?.data);
  const {
    cumulativeOrderList,
    cumulativeOrderListTotal,
    hasCumulativeOrderListError,
    isCumulativeOrderListLoading,
    cumulativeOrderListError,
  } = useCumulativeOrderListSelector();
  const payload = useSelector((state: any) => state.orders.cumulativeList?.payload);

  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(
      doGetCumulativeOrderList({
        params: { accountId: account.accountId },
        queryParams: {
          ...queryParamsDto,
          pageIndex: 0,
          pageSize: 50,
        },
      }),
    );
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!orderListRaw && !isCumulativeOrderListLoading && !hasCumulativeOrderListError) {
      fetchList();

      return;
    }

    if (
      refetchOnChange ||
      !isEqual(payload, {
        params: { accountId: account?.accountId },
        queryParams: { ...queryParamsDto, pageIndex: 0, pageSize: 50 },
      })
    ) {
      fetchList();
    }
  }, dependency);

  return {
    cumulativeOrderList,
    isCumulativeOrderListLoading,
    hasCumulativeOrderListError,
    cumulativeOrderListError,
    cumulativeOrderListTotal,
  };
};
